import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { JsonLd } from '@/components/feature/JsonLd';
import { getArticleBySlug, getLatestArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = getLatestArticles(4).filter((a) => a.slug !== slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryNameMap: Record<string, string> = {
    keji: '科技', shenghuo: '生活', yule: '娱乐', caijing: '财经', jiaoyu: '教育', jiankang: '健康',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://17c.cn/' },
      { '@type': 'ListItem', position: 2, name: categoryNameMap[article.category], item: `https://17c.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://17c.cn/article/${article.slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.imageUrl,
    url: `https://17c.cn/article/${article.slug}`,
    publisher: { '@type': 'Organization', name: '17c.cn', url: 'https://17c.cn' },
  };

  return (
    <>
      <ScrollToTop />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              {categoryNameMap[article.category]}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <div className="max-w-[800px] mx-auto">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                {categoryNameMap[article.category]}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-5">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mb-8 pb-8 border-b border-background-200">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                <p className="text-xs text-foreground-500">{article.date}</p>
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="article-content prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200 max-w-[800px] mx-auto">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticles.slice(0, 4).map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group flex gap-4 p-4 rounded-lg bg-background-100 hover:bg-accent-50/50 transition-colors"
                  >
                    <div className="w-[100px] h-[70px] flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {rel.title}
                      </h3>
                      <p className="mt-1 text-[11px] text-foreground-400">{rel.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}